import { createContext, useCallback, useContext, useState } from 'react';
import Button from './Button';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TOAST_STYLES: Record<ToastType, { box: string; icon: string }> = {
  success: {
    box: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    icon: 'ri-checkbox-circle-fill text-emerald-500',
  },
  error: {
    box: 'bg-red-50 border-red-200 text-red-800',
    icon: 'ri-error-warning-fill text-red-500',
  },
  info: {
    box: 'bg-gray-50 border-gray-200 text-gray-800',
    icon: 'ri-information-fill text-gray-500',
  },
  warning: {
    box: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: 'ri-alert-fill text-amber-500',
  },
};

let toastId = 0;

/**
 * アプリ全体で使うトースト通知。
 * alert() の代わりに showToast(message, type) を呼ぶことで、
 * 統一された見た目・挙動のフィードバックを表示する。
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'success') => {
      const id = ++toastId;
      setToasts((prev) => [...prev.slice(-4), { id, type, message }]);
      window.setTimeout(() => removeToast(id), 4000);
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] space-y-2 pointer-events-none">
        {toasts.map((t) => {
          const s = TOAST_STYLES[t.type];
          return (
            <div
              key={t.id}
              className={`${s.box} border rounded-lg px-4 py-3 shadow-sm flex items-start gap-2.5 min-w-[260px] max-w-sm feedback-fade-in pointer-events-auto`}
              role="status"
            >
              <i className={`${s.icon} text-lg mt-0.5 flex-shrink-0`}></i>
              <p className="text-sm font-medium flex-1 break-words">{t.message}</p>
              <Button
                variant="ghost"
                size="xs"
                iconOnly
                icon="ri-close-line"
                onClick={() => removeToast(t.id)}
                aria-label="閉じる"
                className="flex-shrink-0"
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}